// ═══════════════════════════════════════════════════════════════
// Impact — Dashboard
// Overview stats, recent assessments, map preview, quick actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Stack, Button,
  ThemeIcon, Skeleton, Center, Tooltip,
} from '@mantine/core';
import {
  IconHome, IconAlertTriangle, IconBolt,
  IconClipboardCheck, IconMap,
  IconArrowRight,
} from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import AssessmentMap from './components/AssessmentMap';

const CATEGORY_META: Record<string, { label: string; color: string; icon: typeof IconHome }> = {
  affected:     { label: 'Affected',     color: '#16a34a', icon: IconHome },
  minor:        { label: 'Minor',        color: '#d97706', icon: IconHome },
  major:        { label: 'Major',        color: '#ea580c', icon: IconAlertTriangle },
  destroyed:    { label: 'Destroyed',    color: '#dc2626', icon: IconAlertTriangle },
  inaccessible: { label: 'Inaccessible', color: '#7c3aed', icon: IconAlertTriangle },
};

export default function ImpactDashboard() {
  const navigate = useNavigate();
  const { assessments, isLoading, loadAssessments } = useImpactStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  // Filter assessments for the active incident
  const incidentAssessments = useMemo(() => {
    if (!activeIncident) return assessments;
    return assessments.filter(a => String(a.incidentId) === String(activeIncident.id));
  }, [assessments, activeIncident]);

  // Recent assessments sorted by date
  const recent = useMemo(() => {
    return [...incidentAssessments]
      .sort((a, b) => (b.datecreated ?? '').localeCompare(a.datecreated ?? ''))
      .slice(0, 5);
  }, [incidentAssessments]);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    incidentAssessments.forEach(a => {
      const cat = a.damageCategory || 'unknown';
      counts[cat] = (counts[cat] || 0) + (a.homeCount || 1);
    });
    return counts;
  }, [incidentAssessments]);

  // Assessments with coordinates for map
  const mappable = useMemo(() => {
    return incidentAssessments.filter(a => a.latitude && a.longitude);
  }, [incidentAssessments]);

  const totalHomes = incidentAssessments.reduce((sum, a) => sum + (a.homeCount || 1), 0);
  const totalRecords = incidentAssessments.length;

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed" ta="center" maw={460}>
            Damage assessments are scoped to an incident. Pick one from the
            incident selector in the top bar, or visit the Incident Hub to create one.
          </Text>
          <Button mt="sm" onClick={() => navigate('/incidents')}>Go to Incident Hub</Button>
        </Stack>
      </Center>
    );
  }

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">Impact Assessment</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — {totalRecords} assessments, {totalHomes} homes
          </Text>
        </div>
        <Group gap="xs">
          <Tooltip label="Multi-step form with residence details, habitability, insurance — for thorough assessments" multiline w={280} withArrow>
            <Button
              variant="light"
              leftSection={<IconClipboardCheck size={14} />}
              onClick={() => navigate('/impact/detailed')}
              size="sm"
            >
              Detailed Capture
            </Button>
          </Tooltip>
          <Tooltip label="30-second field entry — photo, location, damage category, and go" multiline w={240} withArrow>
            <Button
              leftSection={<IconBolt size={14} />}
              onClick={() => navigate('/impact/capture')}
              size="sm"
            >
              Quick Capture
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height={120} radius="sm" />)}
        </SimpleGrid>
      ) : (
        <>
          {/* Summary Stat Cards */}
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
            {[
              { label: 'Total Assessments', value: totalRecords, color: '#2C01D6', sub: 'Records in database' },
              { label: 'Total Homes', value: totalHomes, color: '#16a34a', sub: 'Including bulk entries' },
              { label: 'Geolocated', value: mappable.length, color: '#7c3aed', sub: 'With coordinates' },
              { label: 'Major / Destroyed', value: (categoryCounts['destroyed'] || 0) + (categoryCounts['major'] || 0), color: '#dc2626', sub: 'Severe damage' },
            ].map(s => (
              <Paper key={s.label} p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${s.color}` }}>
                <Text size="xs" c="dimmed">{s.label}</Text>
                <Text fz={28} fw={700} style={{ color: s.color }}>{s.value}</Text>
                <Text size="xs" c="dimmed">{s.sub}</Text>
              </Paper>
            ))}
          </SimpleGrid>

          {/* Damage Categories (full width — Assessment Types removed) */}
          <Paper p="sm" radius="sm" withBorder mb="md">
            <Text fw={600} size="sm" mb="xs">Damage Categories</Text>
            {Object.keys(categoryCounts).length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="sm">No assessments recorded yet.</Text>
            ) : (
              <Group gap="md">
                {Object.entries(categoryCounts).map(([cat, count]) => {
                  const meta = CATEGORY_META[cat] || { label: cat, color: '#64748b', icon: IconHome };
                  return (
                    <Group key={cat} gap={6}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color }} />
                      <Text size="sm" fw={500}>{meta.label}</Text>
                      <Badge size="sm" variant="light" style={{ color: meta.color }}>{count}</Badge>
                    </Group>
                  );
                })}
              </Group>
            )}
          </Paper>

          {/* Map + Recent Assessments */}
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            {/* Map Preview */}
            <Paper p="md" radius="sm" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Assessment Map</Text>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/impact/map')}
                >
                  Full Map
                </Button>
              </Group>
              {mappable.length === 0 ? (
                <Center py="xl">
                  <Stack align="center" gap="xs">
                    <ThemeIcon size={40} radius="xl" variant="light" color="gray">
                      <IconMap size={20} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">No geolocated assessments yet.</Text>
                  </Stack>
                </Center>
              ) : (
                <div style={{ height: 300 }}>
                  <AssessmentMap assessments={mappable} height={300} />
                </div>
              )}
            </Paper>

            {/* Recent Assessments */}
            <Paper p="md" radius="sm" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Recent Assessments</Text>
                <Button
                  variant="subtle"
                  size="xs"
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => navigate('/impact/assessments')}
                >
                  View All
                </Button>
              </Group>
              {recent.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  No assessments yet. Start a capture to get started.
                </Text>
              ) : (
                <Stack gap="xs">
                  {recent.map(a => {
                    const catMeta = CATEGORY_META[a.damageCategory] || { label: a.damageCategory, color: '#64748b' };
                    return (
                      <Paper
                        key={a.id}
                        p="sm"
                        radius="sm"
                        withBorder
                        style={{ borderLeft: `3px solid ${catMeta.color}`, cursor: 'pointer' }}
                        onClick={() => navigate(`/impact/assessment/${a.id}`)}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Group gap="xs" mb={2}>
                              <Badge size="xs" style={{ background: `${catMeta.color}15`, color: catMeta.color }}>
                                {catMeta.label}
                              </Badge>
                              <Badge size="xs" variant="outline" color="gray" tt="capitalize">
                                {a.assessmentType || 'unknown'}
                              </Badge>
                            </Group>
                            <Text size="sm" fw={500} lineClamp={1}>
                              {a.address || 'No address'}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {a.county ? `${a.county} County` : ''}{a.severity ? ` — Severity ${a.severity}` : ''}
                            </Text>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <Text size="xs" c="dimmed">{a.assessorName || 'Unknown'}</Text>
                            <Text size="xs" c="dimmed">{a.datecreated || ''}</Text>
                          </div>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </SimpleGrid>

        </>
      )}
    </div>
  );
}
